import { useState, useRef, useEffect } from "react"
import { Sparkles, Send, Bot, AlertTriangle, Loader2, CheckCircle2 } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { aiChat } from "@/lib/api/hive"
import hive from "@/lib/api/hive"
import type { ChatMessage, TransactionUpdate, TransactionDelete } from "@/types/ai"

interface AIChatDrawerProps {
  portfolioId: string
  portfolioName: string
  token: string
  onTransactionAdded: () => void
}

const SUGGESTION_CHIPS = [
  "How is my portfolio doing?",
  "What's my total return this year?",
  "Log a trade",
]

function TransactionCard({
  message,
  portfolioId,
  token,
  onTransactionAdded,
}: {
  message: ChatMessage
  portfolioId: string
  token: string
  onTransactionAdded: () => void
}) {
  const [adding, setAdding] = useState(false)
  const [added, setAdded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const tx = message.transaction!

  const handleAdd = async () => {
    if (!tx.ticker || !tx.exchange) return
    setAdding(true)
    setError(null)
    try {
      await hive.createTransaction(
        portfolioId,
        {
          ticker: tx.ticker,
          exchange: tx.exchange,
          type: tx.type,
          shares: tx.shares,
          price: tx.price ?? undefined,
          date: tx.date ?? undefined,
        },
        token,
      )
      setAdded(true)
      onTransactionAdded()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add transaction")
    } finally {
      setAdding(false)
    }
  }

  if (added) {
    return (
      <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        Transaction added
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-slate-800 border border-sky-900/10 dark:border-sky-100/10 rounded-xl overflow-hidden text-sm w-full max-w-[300px]">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-sky-900/10 dark:border-sky-100/10">
        <span
          className={cn(
            "text-xs font-bold px-1.5 py-0.5 rounded",
            tx.type === "BUY"
              ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400"
              : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
          )}
        >
          {tx.type}
        </span>
        <span className="font-semibold text-sky-900 dark:text-sky-100">
          {tx.ticker ?? tx.company_name}
        </span>
        {tx.ticker && tx.ticker !== tx.company_name && (
          <span className="text-xs text-muted-foreground truncate">{tx.company_name}</span>
        )}
      </div>

      {/* Details */}
      <div className="px-3 py-2 space-y-1 text-muted-foreground">
        <div className="flex justify-between">
          <span>Shares</span>
          <span className="font-medium text-foreground">{tx.shares}</span>
        </div>
        {tx.price != null && (
          <div className="flex justify-between">
            <span>Price</span>
            <span className="font-medium text-foreground">${tx.price.toFixed(2)}</span>
          </div>
        )}
        {tx.date && (
          <div className="flex justify-between">
            <span>Date</span>
            <span className="font-medium text-foreground">{tx.date.split("T")[0]}</span>
          </div>
        )}
        {tx.exchange && (
          <div className="flex justify-between">
            <span>Exchange</span>
            <span className="font-medium text-foreground">{tx.exchange}</span>
          </div>
        )}
      </div>

      {/* Unresolved warning */}
      {!tx.resolved && (
        <div className="px-3 pb-2 text-xs text-amber-600 dark:text-amber-400">
          Ticker couldn't be resolved automatically. Please add manually.
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="px-3 pb-2 text-xs text-red-600 dark:text-red-400">{error}</div>
      )}

      {/* Actions */}
      <div className="flex gap-2 px-3 py-2 border-t border-sky-900/10 dark:border-sky-100/10">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 text-xs"
          onClick={() => {
            // Opening TransactionForm manually is handled by closing the drawer
            // and letting user use the existing form. No direct integration needed.
          }}
          disabled={adding}
        >
          Edit manually
        </Button>
        <Button
          size="sm"
          className="flex-1 text-xs bg-sky-600 hover:bg-sky-700 text-white"
          onClick={handleAdd}
          disabled={adding || !tx.resolved}
        >
          {adding ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            "Add Transaction →"
          )}
        </Button>
      </div>
    </div>
  )
}

function UpdateTransactionCard({
  update,
  portfolioId,
  token,
  onTransactionAdded,
}: {
  update: TransactionUpdate
  portfolioId: string
  token: string
  onTransactionAdded: () => void
}) {
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConfirm = async () => {
    setSaving(true)
    setError(null)
    try {
      await hive.updateTransaction(
        portfolioId,
        update.transaction_id,
        {
          price: update.new_price,
          date: update.new_date,
          shares: update.new_shares,
          type: update.new_type as "BUY" | "SELL" | undefined,
        },
        token,
      )
      setSaved(true)
      onTransactionAdded()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update transaction")
    } finally {
      setSaving(false)
    }
  }

  if (saved) {
    return (
      <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        Transaction updated
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-slate-800 border border-sky-900/10 dark:border-sky-100/10 rounded-xl overflow-hidden text-sm w-full max-w-[300px]">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-sky-900/10 dark:border-sky-100/10">
        <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-400">
          EDIT
        </span>
        <span className="font-semibold text-sky-900 dark:text-sky-100">{update.ticker}</span>
        <span className="text-xs text-muted-foreground">{update.exchange}</span>
      </div>
      <div className="px-3 py-2 space-y-1 text-muted-foreground">
        {update.new_price != null && (
          <div className="flex justify-between">
            <span>Price</span>
            <span className="font-medium text-foreground">
              <span className="line-through text-muted-foreground mr-1">${update.current_price.toFixed(2)}</span>
              ${update.new_price.toFixed(2)}
            </span>
          </div>
        )}
        {update.new_date != null && (
          <div className="flex justify-between">
            <span>Date</span>
            <span className="font-medium text-foreground">
              <span className="line-through text-muted-foreground mr-1">{update.current_date}</span>
              {update.new_date}
            </span>
          </div>
        )}
        {update.new_shares != null && (
          <div className="flex justify-between">
            <span>Shares</span>
            <span className="font-medium text-foreground">
              <span className="line-through text-muted-foreground mr-1">{update.current_shares}</span>
              {update.new_shares}
            </span>
          </div>
        )}
        {update.new_type != null && (
          <div className="flex justify-between">
            <span>Type</span>
            <span className="font-medium text-foreground">
              <span className="line-through text-muted-foreground mr-1">{update.current_type}</span>
              {update.new_type}
            </span>
          </div>
        )}
      </div>
      {error && <div className="px-3 pb-2 text-xs text-red-600 dark:text-red-400">{error}</div>}
      <div className="flex gap-2 px-3 py-2 border-t border-sky-900/10 dark:border-sky-100/10">
        <Button size="sm" className="flex-1 text-xs bg-sky-600 hover:bg-sky-700 text-white" onClick={handleConfirm} disabled={saving}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Confirm Update →"}
        </Button>
      </div>
    </div>
  )
}

function DeleteTransactionCard({
  del,
  portfolioId,
  token,
  onTransactionAdded,
}: {
  del: TransactionDelete
  portfolioId: string
  token: string
  onTransactionAdded: () => void
}) {
  const [deleting, setDeleting] = useState(false)
  const [deleted, setDeleted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleDelete = async () => {
    setDeleting(true)
    setError(null)
    try {
      await hive.deleteTransaction(portfolioId, del.transaction_id, token)
      setDeleted(true)
      onTransactionAdded()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete transaction")
    } finally {
      setDeleting(false)
    }
  }

  if (deleted) {
    return (
      <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        Transaction removed
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-slate-800 border border-red-200 dark:border-red-900/40 rounded-xl overflow-hidden text-sm w-full max-w-[300px]">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-red-200 dark:border-red-900/40">
        <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400">
          REMOVE
        </span>
        <span className="font-semibold text-sky-900 dark:text-sky-100">{del.ticker}</span>
        <span className="text-xs text-muted-foreground">{del.exchange}</span>
      </div>
      <div className="px-3 py-2 space-y-1 text-muted-foreground">
        <div className="flex justify-between">
          <span>Type</span><span className="font-medium text-foreground">{del.type}</span>
        </div>
        <div className="flex justify-between">
          <span>Shares</span><span className="font-medium text-foreground">{del.shares}</span>
        </div>
        <div className="flex justify-between">
          <span>Price</span><span className="font-medium text-foreground">${del.price.toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span>Date</span><span className="font-medium text-foreground">{del.date}</span>
        </div>
      </div>
      {error && <div className="px-3 pb-2 text-xs text-red-600 dark:text-red-400">{error}</div>}
      <div className="flex gap-2 px-3 py-2 border-t border-red-200 dark:border-red-900/40">
        <Button size="sm" variant="destructive" className="flex-1 text-xs" onClick={handleDelete} disabled={deleting}>
          {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Remove Transaction"}
        </Button>
      </div>
    </div>
  )
}

function MessageBubble({
  message,
  portfolioId,
  token,
  onTransactionAdded,
}: {
  message: ChatMessage
  portfolioId: string
  token: string
  onTransactionAdded: () => void
}) {
  const isUser = message.role === "user"

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-sky-600 text-white rounded-2xl rounded-tr-sm px-3 py-2 text-sm">
          {message.text}
        </div>
      </div>
    )
  }

  if (message.isQuotaError) {
    return (
      <div className="flex gap-2 items-start">
        <div className="shrink-0 h-6 w-6 rounded-full bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center mt-0.5">
          <Bot className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" />
        </div>
        <div className="max-w-[80%] bg-white dark:bg-slate-800 border border-amber-200 dark:border-amber-800 rounded-2xl rounded-tl-sm px-3 py-2 text-sm flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
          <span className="text-amber-700 dark:text-amber-400">
            Monthly AI limit reached. Upgrade to Pro for unlimited access.
          </span>
        </div>
      </div>
    )
  }

  if (message.transaction) {
    return (
      <div className="flex gap-2 items-start">
        <div className="shrink-0 h-6 w-6 rounded-full bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center mt-0.5">
          <Bot className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" />
        </div>
        <div className="flex flex-col gap-2">
          {message.text && (
            <div className="bg-white dark:bg-slate-800 border border-sky-900/10 dark:border-sky-100/10 rounded-2xl rounded-tl-sm px-3 py-2 text-sm text-foreground">
              {message.text}
            </div>
          )}
          <TransactionCard message={message} portfolioId={portfolioId} token={token} onTransactionAdded={onTransactionAdded} />
        </div>
      </div>
    )
  }

  if (message.transactionUpdate) {
    return (
      <div className="flex gap-2 items-start">
        <div className="shrink-0 h-6 w-6 rounded-full bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center mt-0.5">
          <Bot className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" />
        </div>
        <UpdateTransactionCard update={message.transactionUpdate} portfolioId={portfolioId} token={token} onTransactionAdded={onTransactionAdded} />
      </div>
    )
  }

  if (message.transactionDelete) {
    return (
      <div className="flex gap-2 items-start">
        <div className="shrink-0 h-6 w-6 rounded-full bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center mt-0.5">
          <Bot className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" />
        </div>
        <DeleteTransactionCard del={message.transactionDelete} portfolioId={portfolioId} token={token} onTransactionAdded={onTransactionAdded} />
      </div>
    )
  }

  return (
    <div className="flex gap-2 items-start">
      <div className="shrink-0 h-6 w-6 rounded-full bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center mt-0.5">
        <Bot className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" />
      </div>
      <div className="max-w-[80%] bg-white dark:bg-slate-800 border border-sky-900/10 dark:border-sky-100/10 rounded-2xl rounded-tl-sm px-3 py-2 text-sm text-foreground">
        {message.text}
      </div>
    </div>
  )
}

function LoadingBubble() {
  return (
    <div className="flex gap-2 items-start">
      <div className="shrink-0 h-6 w-6 rounded-full bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center mt-0.5">
        <Bot className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" />
      </div>
      <div className="bg-white dark:bg-slate-800 border border-sky-900/10 dark:border-sky-100/10 rounded-2xl rounded-tl-sm px-4 py-3">
        <div className="flex gap-1 items-center h-4">
          <span className="h-1.5 w-1.5 bg-sky-400 rounded-full animate-bounce [animation-delay:0ms]" />
          <span className="h-1.5 w-1.5 bg-sky-400 rounded-full animate-bounce [animation-delay:150ms]" />
          <span className="h-1.5 w-1.5 bg-sky-400 rounded-full animate-bounce [animation-delay:300ms]" />
        </div>
      </div>
    </div>
  )
}

export function AIChatDrawer({ portfolioId, portfolioName, token, onTransactionAdded }: AIChatDrawerProps) {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, isLoading])

  // Focus input when drawer opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  const sendMessage = async (text: string) => {
    const trimmed = text.trim()
    if (!trimmed || isLoading) return

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      text: trimmed,
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    setInput("")
    setIsLoading(true)

    try {
      const response = await aiChat(trimmed, portfolioId, token)

      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        text: response.message ?? undefined,
        transaction: response.transaction ?? undefined,
        transactionUpdate: response.transaction_update ?? undefined,
        transactionDelete: response.transaction_delete ?? undefined,
        timestamp: new Date(),
      }

      setMessages((prev) => [...prev, assistantMessage])
    } catch (e) {
      const isQuota = (e as Error & { isQuotaError?: boolean }).isQuotaError === true
      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        isQuotaError: isQuota,
        text: isQuota ? undefined : (e instanceof Error ? e.message : "Something went wrong. Please try again."),
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, assistantMessage])
    } finally {
      setIsLoading(false)
      inputRef.current?.focus()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      sendMessage(input)
    }
  }

  const handleChip = (chip: string) => {
    setInput(chip)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="hover:bg-sky-100 dark:hover:bg-sky-900/30 border-sky-200 dark:border-sky-800">
          <Sparkles className="h-4 w-4 mr-1.5 text-sky-600 dark:text-sky-400" />
          <span className="text-sky-700 dark:text-sky-300">Ask AI</span>
        </Button>
      </SheetTrigger>

      <SheetContent
        side="right"
        className="w-[400px] sm:w-[480px] flex flex-col p-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md"
      >
        <SheetHeader className="px-4 py-3 border-b border-sky-900/10 dark:border-sky-100/10 shrink-0">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-full bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-sky-600 dark:text-sky-400" />
            </div>
            <div>
              <SheetTitle className="text-sky-950 dark:text-sky-50 text-sm leading-tight">
                AI Portfolio Assistant
              </SheetTitle>
              <SheetDescription className="text-xs leading-tight">
                {portfolioName} · Ask about your portfolio or log a trade
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Message list */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
          {messages.length === 0 && !isLoading && (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center py-8">
              <div className="h-12 w-12 rounded-full bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center">
                <Bot className="h-6 w-6 text-sky-600 dark:text-sky-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-sky-900 dark:text-sky-100 mb-1">
                  What can I help you with?
                </p>
                <p className="text-xs text-muted-foreground">
                  Ask portfolio questions or log a trade in plain English.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center mt-2">
                {SUGGESTION_CHIPS.map((chip) => (
                  <button
                    key={chip}
                    onClick={() => handleChip(chip)}
                    className="text-xs px-3 py-1.5 rounded-full border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-900/40 transition-colors"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              portfolioId={portfolioId}
              token={token}
              onTransactionAdded={() => {
                onTransactionAdded()
              }}
            />
          ))}

          {isLoading && <LoadingBubble />}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="px-4 py-3 border-t border-sky-900/10 dark:border-sky-100/10 shrink-0">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              placeholder="Ask something or describe a trade..."
              className={cn(
                "flex-1 text-sm rounded-xl border border-sky-900/20 dark:border-sky-100/20",
                "bg-white/60 dark:bg-black/20 backdrop-blur-sm",
                "px-3 py-2 outline-none placeholder:text-muted-foreground",
                "focus:ring-1 focus:ring-sky-400 dark:focus:ring-sky-600",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            />
            <Button
              size="sm"
              className="shrink-0 bg-sky-600 hover:bg-sky-700 text-white rounded-xl px-3"
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
